import { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../../styles/login.css";
import { useNavigate } from "react-router-dom";

const API = "/api";

const isBeeline = (phone: string) => {
  const digits = phone.replace(/\D/g, '');
  const local = digits.startsWith('7') ? digits.slice(1) : digits;
  return local.startsWith('701') || local.startsWith('700') || local.startsWith('771');
};

const CODE_LENGTH = 4;

function CodeBoxes({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  function handleKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (!value[i] && i > 0) {
        const next = value.split('');
        next[i - 1] = '';
        onChange(next.join(''));
        refs.current[i - 1]?.focus();
      } else {
        const next = value.split('');
        next[i] = '';
        onChange(next.join(''));
      }
      e.preventDefault();
    }
  }

  function handleInput(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) return;
    const digit = raw[raw.length - 1];
    const next = value.split('');
    next[i] = digit;
    const joined = next.join('');
    onChange(joined.padEnd(CODE_LENGTH, '').slice(0, CODE_LENGTH));
    if (i < CODE_LENGTH - 1) refs.current[i + 1]?.focus();
  }

  function handlePaste(e: React.ClipboardEvent) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (text) {
      onChange(text.padEnd(CODE_LENGTH, '').slice(0, CODE_LENGTH));
      refs.current[Math.min(text.length, CODE_LENGTH - 1)]?.focus();
    }
    e.preventDefault();
  }

  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '8px 0' }}>
      {Array.from({ length: CODE_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={el => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] ?? ''}
          onChange={e => handleInput(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={() => refs.current[i]?.select()}
          style={{
            flex: '1 1 0', minWidth: 0, maxWidth: 52, height: 52,
            textAlign: 'center', fontSize: 22, fontWeight: 700,
            background: value[i] ? 'rgba(99,102,241,0.15)' : '#111827',
            border: `2px solid ${value[i] ? '#6366f1' : '#374151'}`,
            borderRadius: 10, color: '#fff', outline: 'none',
            boxSizing: 'border-box', transition: 'all 0.15s',
          }}
        />
      ))}
    </div>
  );
}


export default function LoginForm({ onLogin }: { onLogin: (token: string) => void; theme?: string; setTheme?: (t: any) => void }) {
  const [step, setStep] = useState<"input" | "password" | "code" | "forgot" | "forgot-sent">("input");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [channel, setChannel] = useState<"whatsapp" | "sms" | "dev" | "">("");
  const [smsLoading, setSmsLoading] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (step !== "code") return;
    if (timer <= 0) { setCanResend(true); return; }
    const t = setTimeout(() => setTimer(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [timer, step]);

  useEffect(() => {
    if (step !== "code" || channel !== "whatsapp") return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.post(`${API}/auth/wa-status`, { phone: identifier });
        const st = res.data?.status;
        if (st === 'delivered') { setInfo("✓ Доставлено в WhatsApp"); clearInterval(pollRef.current!); }
        else if (st === 'sms_sent') { setInfo("📨 WhatsApp недоступен — отправили SMS"); setChannel("sms"); clearInterval(pollRef.current!); }
      } catch {}
    }, 5000);
    return () => clearInterval(pollRef.current!);
  }, [step, channel, identifier]);

  useEffect(() => {
    if (step === "code" && code.replace(/\D/g,'').length === CODE_LENGTH) handleLogin();
  }, [code]);

  const handleNext = async () => {
    setError(""); setInfo("");
    const isPhone = /^\+?\d{10,15}$/.test(identifier.replace(/[\s()-]/g, ''));
    const isEmail = /\S+@\S+\.\S+/.test(identifier);
    if (isPhone) {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/auth/send-code`, { phone: identifier });
        setChannel(res.data.channel);
        setStep("code"); setTimer(60); setCanResend(false); setCode("");
        setInfo(res.data.channel === 'whatsapp' ? "Код отправлен в WhatsApp" : "Код отправлен по SMS");
      } catch (e: any) {
        setError(e.response?.data?.message || "Ошибка отправки");
      } finally { setLoading(false); }
      return;
    }
    if (isEmail) { setStep("password"); return; }
    setError("Введите номер телефона или email");
  };

  const handleLogin = async () => {
    setError("");
    if (step === "password") {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/auth/login-email`, { email: identifier.trim().toLowerCase(), password });
        onLogin(res.data.token); navigate("/deals");
      } catch (e: any) {
        setError(e.response?.data?.message || "Неверный email или пароль");
      } finally { setLoading(false); }
      return;
    }
    if (step === "code") {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/auth/verify`, { phone: identifier, code });
        onLogin(res.data.token); navigate("/deals");
      } catch (e: any) {
        setError(e.response?.data?.message || "Неверный код"); setCode("");
      } finally { setLoading(false); }
    }
  };

  const handleForgot = async () => {
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: identifier, type: 'crm' });
      setStep('forgot-sent');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Ошибка отправки');
    } finally { setLoading(false); }
  };

  const handleSendSms = async () => {
    setSmsLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/send-sms`, { phone: identifier });
      setChannel("sms"); setInfo("Код отправлен по SMS"); setCanResend(false); setTimer(60);
    } catch (e: any) {
      setError(e.response?.data?.message || "Ошибка отправки SMS");
    } finally { setSmsLoading(false); }
  };

  return (
    <div className="login-wrapper">
      <div className="login-box">

        {/* Логотип */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logo-keyoff-group.png" alt="Keyoff Group" style={{ width: 150, height: 150, objectFit: 'contain', marginBottom: 2 }} />
          <div style={{ fontSize: 20, fontWeight: 800, color: '#818cf8', letterSpacing: '-0.5px' }}>Keyoff Group CRM</div>
          <div style={{ fontSize: 12, color: '#4b5563', marginTop: 3 }}>Система управления продажами</div>
        </div>

        {/* STEP 1 */}
        {step === "input" && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>👋</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>Добро пожаловать</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Введите телефон для входа по WhatsApp<br />или email для входа по паролю</div>
            </div>
            <input className="input" placeholder="+7 700 000 00 00 или email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="text" autoComplete="username"
              value={identifier} onChange={e => setIdentifier(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleNext()}
              autoFocus style={{ fontSize: 14, padding: '12px 14px' }} />
            {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
            <button className="btn" onClick={handleNext} disabled={loading}
              style={{ marginTop: 14, padding: 12, fontSize: 14, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Отправка...' : 'Продолжить →'}
            </button>
          </>
        )}

        {/* STEP 2 */}
        {step === "password" && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>🔒</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>Вход по паролю</div>
              <div style={{ fontSize: 12, color: '#818cf8', marginTop: 6, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, padding: '5px 12px', display: 'inline-block' }}>
                {identifier}
              </div>
            </div>
            <input className="input" type="password" placeholder="Введите пароль" autoCapitalize="none" autoCorrect="off" autoComplete="current-password"
              value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              autoFocus style={{ fontSize: 14, padding: '12px 14px' }} />
            {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
            <button className="btn" onClick={handleLogin} disabled={loading}
              style={{ marginTop: 14, padding: 12, fontSize: 14, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Вход...' : 'Войти'}
            </button>
            <button style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 13, cursor: 'pointer', marginTop: 10, textDecoration: 'underline' }}
              onClick={() => { setStep('forgot'); setError(''); }}>
              Забыли пароль?
            </button>
            <button className="btn-back" onClick={() => { setStep("input"); setError(""); }}>
              ← Изменить email
            </button>
          </>
        )}

        {/* FORGOT */}
        {step === "forgot" && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>📧</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>Сброс пароля</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Введите email вашего аккаунта</div>
            </div>
            <input className="input" type="email" placeholder="your@email.com" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="email"
              value={identifier} onChange={e => setIdentifier(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleForgot()}
              autoFocus style={{ fontSize: 14, padding: '12px 14px' }} />
            {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
            <button className="btn" onClick={handleForgot} disabled={loading}
              style={{ marginTop: 14, padding: 12, fontSize: 14 }}>
              {loading ? 'Отправка...' : 'Отправить ссылку'}
            </button>
            <button className="btn-back" onClick={() => { setStep("password"); setError(""); }}>
              ← Назад
            </button>
          </>
        )}

        {step === "forgot-sent" && (
          <>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✉️</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#f9fafb', marginBottom: 8 }}>Письмо отправлено</div>
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                Если аккаунт с адресом <span style={{ color: '#818cf8' }}>{identifier}</span> существует — письмо придёт в течение минуты.
              </div>
            </div>
            <button className="btn-back" onClick={() => { setStep("input"); setError(""); }}>
              ← На главную
            </button>
          </>
        )}

        {/* STEP 3 */}
        {step === "code" && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>{channel === 'whatsapp' ? '💬' : '📨'}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#f9fafb' }}>
                {channel === 'whatsapp' ? 'Код в WhatsApp' : 'Код по SMS'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Отправили на <span style={{ color: '#818cf8', fontWeight: 600 }}>{identifier}</span>
              </div>
            </div>

            {info && <div className="login-info" style={{ marginBottom: 12, textAlign: 'center', fontSize: 12 }}>{info}</div>}

            <CodeBoxes value={code} onChange={setCode} />

            {error && <div className="error" style={{ marginTop: 8, textAlign: 'center' }}>{error}</div>}

            <button className="btn" onClick={handleLogin} disabled={loading || code.length < CODE_LENGTH}
              style={{ marginTop: 14, padding: 12, fontSize: 14, opacity: (loading || code.length < CODE_LENGTH) ? 0.5 : 1 }}>
              {loading ? 'Проверка...' : 'Подтвердить вход'}
            </button>

            {!canResend ? (
              <div className="timer" style={{ marginTop: 10, fontSize: 12 }}>Повторная отправка через {timer} сек</div>
            ) : (
              <div className="resend-block" style={{ marginTop: 10 }}>
                <button className="btn-outline" onClick={handleNext} style={{ fontSize: 13 }}>
                  💬 Повторно в WhatsApp
                </button>
                {!isBeeline(identifier) ? (
                  <button className="btn-outline" onClick={handleSendSms} disabled={smsLoading} style={{ fontSize: 13 }}>
                    {smsLoading ? "Отправка..." : "📨 Получить SMS"}
                  </button>
                ) : (
                  <div className="beeline-note">⚠️ На номера Билайн SMS не может быть отправлено</div>
                )}
              </div>
            )}

            <button className="btn-back" onClick={() => { setStep("input"); setError(""); setInfo(""); setCode(""); }}>
              ← Изменить номер
            </button>
          </>
        )}
      </div>
    </div>
  );
}
